import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { userlogout, acquireLogoutLock } from '../../../Shared/Slice/AuthSlice';
import showToast from '../../../Shared/Utils/ToastNotification';
// import Footer from '../../../Components/Footer/Footer';
import Service from '../Profile/services/Service';
import ApiUrlConstant from '../../../Shared/Utils/UrlConstants';
import { axiosInstance } from '../../../Interceptors/axiosInterceptor';
import './AccountInfo.scss';

const AccountInfo = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const response = await Service.userProfile();
      setProfileData(response.data);
    } catch (error) {
      showToast('error', 'Error loading profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setEditData({
      first_name: profileData?.first_name || '',
      last_name: profileData?.last_name || '',
      phone_number: profileData?.phone_number || '',
      address: profileData?.address || '',
      city: profileData?.city || '',
      zip_code: profileData?.zip_code || '',
      enable_2fa: profileData?.enable_2fa || false,
    });
    setIsEditMode(true);
  };

  const handleSaveEmail = async () => {
    try {
      await Service.updateUserProfile({ email: newEmail });
      setProfileData({ ...profileData, email: newEmail });
      setNewEmail('');
      showToast('success', 'Email updated successfully');
    } catch (error) {
      showToast('error', 'Failed to update email');
    }
  };

  const handleSaveChanges = async () => {
    try {
      await Service.updateUserProfile(editData);
      setProfileData({ ...profileData, ...editData });
      setIsEditMode(false);
      showToast('success', 'Profile updated successfully');
    } catch (error) {
      showToast('error', 'Failed to update profile');
    }
  };

  const handleChangePassword = async () => {
    if (!profileData?.email) {
      showToast('error', 'Email not found');
      return;
    }

    try {
      const response = await axiosInstance.post(ApiUrlConstant.forgotPasswordService(), {
        email: profileData.email,
        redirect_url: `${window.location.origin}/reset-password`,
      });

      if (response.status === 200 || response.status === 201) {
        showToast('success', 'Password reset link sent to your email');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send password reset link';
      showToast('error', errorMessage);
    }
  };

  const handleLogout = () => {
    if (!acquireLogoutLock()) return;
    dispatch(userlogout());
    showToast('info', 'Logged Out Successfully');
    navigate('/login');
  };

  const [activeTab, setActiveTab] = useState('personal');
  return (
    <>
      <div className={`AccountInfo-page ${isMobileDetailOpen ? 'mobile-detail-open' : ''}`}>
        <div className="container">
          <h3 className="section-title">Account info</h3>
          <div className="settings-layout">
            {/* LEFT SIDEBAR */}
            <div className="settings-sidebar">
              <ul className="nav flex-column nav-pills" role="tablist">
                {[
                  { label: 'Personal details', key: 'personal' },
                  { label: 'Billing and payments', key: 'billing' },
                  { label: 'Notifications', key: 'notifications' },
                  { label: 'Privacy and security', key: 'privacy' },
                  { label: 'Connected devices', key: 'devices' },
                ].map((item) => (
                  <li className="nav-item" key={item.key}>
                    <button
                      className={`nav-link ${activeTab === item.key ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab(item.key);
                        setIsMobileDetailOpen(true);
                      }}
                      type="button"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>

              <button className="btn btn-secondary d-flex justify-content-center gap-2 align-items-center" onClick={handleLogout}>
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8 10.1C7.50294 10.1 7.1 10.5029 7.1 11C7.1 11.4971 7.50294 11.9 8 11.9H19C19.4971 11.9 19.9 11.4971 19.9 11C19.9 10.5029 19.4971 10.1 19 10.1H8Z"
                    fill="#EEF3FC"
                  />
                  <path d="M13 5L19 11L13 17" stroke="#EEF3FC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M9 3H3V19H9" stroke="#EEF3FC" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Log out
              </button>
            </div>

            {/* RIGHT CONTENT */}
            <div className="settings-content tab-content">
              {activeTab === 'personal' && (
                <div className="tab-pane-custom">
                  {/* Personal details TAB */}
                  <div className="tab-pane">
                    <div className="content-header mb-5">
                      <button className="mobile-back-btn d-flex d-md-none gap-3 align-items-center" onClick={() => setIsMobileDetailOpen(false)}>
                        <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M6.90332 12.9036L0.903321 6.90356L6.90332 0.903564"
                            stroke="#EEF3FC"
                            strokeWidth="1.80694"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Back
                      </button>
                      <div>
                        <h3>Personal details</h3>
                        <p>Please feel free to edit the information below to ensure your account is completely up to date.</p>
                      </div>
                    </div>

                    {loading ? (
                      <div className="text-center">Loading...</div>
                    ) : (
                      <div className="personal-blk d-flex flex-column">
                        <div className="personal-card">
                          <h4>Details</h4>
                          <p>
                            {profileData?.first_name} {profileData?.last_name} <br />
                            {profileData?.phone_number} <br />
                            {profileData?.address} <br />
                            {profileData?.city}, {profileData?.zip_code}
                          </p>
                          <a href="#" onClick={handleEditClick}>
                            Edit
                          </a>
                        </div>

                        <div className="personal-card">
                          <h4>Login details</h4>

                          <div className="info mb-3">
                            <div className="lable">Email address</div>
                            <div className="value">{profileData?.email}</div>
                          </div>
                          <div className="info">
                            <div className="lable">Password</div>
                            <div className="value">**************</div>
                            <button className="btn btn-link p-0" onClick={handleChangePassword}>
                              Change Password
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(activeTab === 'billing' || activeTab === 'notifications' || activeTab === 'privacy' || activeTab === 'devices') && (
                <div className="tab-pane-custom">
                  <div className="tab-pane">
                    <div className="content-header mb-5">
                      <button className="mobile-back-btn d-flex d-md-none gap-3 align-items-center" onClick={() => setIsMobileDetailOpen(false)}>
                        <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M6.90332 12.9036L0.903321 6.90356L6.90332 0.903564"
                            stroke="#EEF3FC"
                            strokeWidth="1.80694"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Back
                      </button>
                      <div className="text-center">
                        <h3>Coming Soon</h3>
                        <p>This feature is currently under development and will be available soon.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* <Footer /> */}
      </div>

      {/* Edit Profile Modal */}
      {isEditMode && (
        <>
          <div className="modal fade show" style={{ display: 'block' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header" style={{ height: '90px', position: 'relative' }}>
                  <h5 className="modal-title" style={{ color: 'white' }}>
                    Edit Personal Details
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setIsEditMode(false)}
                    style={{ position: 'absolute', top: '10px', right: '15px' }}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label>First Name</label>
                      <input
                        className="form-control"
                        value={editData.first_name}
                        onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>Last Name</label>
                      <input
                        className="form-control"
                        value={editData.last_name}
                        onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                      />
                    </div>
                    <div className="col-12 mb-3">
                      <label>Phone Number</label>
                      <input
                        className="form-control"
                        value={editData.phone_number}
                        onChange={(e) => setEditData({ ...editData, phone_number: e.target.value })}
                      />
                    </div>
                    <div className="col-12 mb-3">
                      <label>Address</label>
                      <input
                        className="form-control"
                        value={editData.address}
                        onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>City</label>
                      <input className="form-control" value={editData.city} onChange={(e) => setEditData({ ...editData, city: e.target.value })} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>Zip Code</label>
                      <input
                        className="form-control"
                        value={editData.zip_code}
                        onChange={(e) => setEditData({ ...editData, zip_code: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={handleSaveChanges}>
                    Save Changes
                  </button>
                  <button className="btn btn-outline" onClick={() => setIsEditMode(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setIsEditMode(false)}></div>
        </>
      )}

      {/* modal here */}
      <div className="modal fade editEmail" id="editEmail" tabIndex="-1" aria-labelledby="editEmailLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              style={{ position: 'absolute', top: '10px', right: '15px', zIndex: 1 }}
            ></button>
            <div className="modal-body">
              <div className="">
                <h3 className="modal-title" id="editEmailLabel">
                  Change your E-mail
                </h3>
              </div>
              <div className="row">
                <div className="col-10 mb-4">
                  <form>
                    <div className="form-group">
                      <label>E-mail address</label>
                      <input
                        className="form-control"
                        type="email"
                        name="email"
                        placeholder="Enter email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                      />
                    </div>
                  </form>
                </div>
                <div className="col-12 d-flex align-items-end gap-3">
                  <button className="btn btn-primary w-fit px-4" onClick={handleSaveEmail}>
                    Save changes
                  </button>
                  <button className="btn btn-outline w-fit px-4" data-bs-dismiss="modal">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountInfo;
